import React, { useEffect, useState } from "react";
import classNames from "classnames";
import Modal from "components/base/Modal";
import css from "../style.scss";
import config from 'config';
import ToastNotification from "../../components/ToastNotification";
import refreshBtn from "assets/images/refreshBtn.svg";
import flippersBtn from "assets/images/flippers.svg";
import AutocompleteDropdown from "../../../components/SearchDropdown";
import Notesimportanticon from "assets/images/Notesimportant.svg";
import ResettableAutocompleteDropdown from "../../../components/SearchDropdown/ResettableAutocompleteDropdown";
import LoadingButton from "components/LoadingButton/LoadingButton";
import Tooltip from '../../components/Tooltip';
import RightArrow from "assets/images/account-page/right-full-arrow.svg";
import AreaCodeUnavailable from "./AreaCodeUnavailable.js";

import { useDispatch, useSelector } from "react-redux";
import {
  selectUserId,
  selectStateAreaCodes,
  selectCallSettingsAvailableOutboundNumbers,
  selectLoadingCallSettingsAvailableOutboundNumbers,
  selectCallSettings,
} from "data/user/selectors";
import { getStateandAreaIntegration, getCallSettingsAvailableOutboundNumbers } from "data/user/actions";
import { getRandomItems } from "utils/array/getRandomItems";
import { replaceCallSettingsOutboundPhoneNumber } from "../../../data/user/actions";

const ReplacePhoneNumber = ({
  isOpen,
  onClose,
  callSettingsId,
  modalType,
  text,
  setText,
  showPassword,
  togglePasswordVisibility,
  integrationId,
}) => {
  const dispatch = useDispatch();

  const {
    constants: {
      API: { PRICING_UPDATE_URL },
    },
  } = config;

  const [selectedState, setSelectedState] = useState(null);
  const [selectedAreaCode, setSelectedAreaCode] = useState(null);
  const [areaCodesForSelectedState, setAreaCodesForSelectedState] = useState([]);
  const [selectedNumbers, setSelectedNumbers] = useState(null);

  const userId = useSelector(selectUserId);
  const stateAreaCodes = useSelector(selectStateAreaCodes);
  const availableOutboundNumbers = useSelector(selectCallSettingsAvailableOutboundNumbers);
  const isLoadingAvailableOutboundNumbers = useSelector(selectLoadingCallSettingsAvailableOutboundNumbers);
  const callSettings = useSelector(selectCallSettings);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [resetKey, setResetKey] = useState(0);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [areaCodeDoesntHaveEnoughNumbersError, setAreaCodeDoesntHaveEnoughNumbersError] = useState(false);
  const [isAfterReplaceError, setIsAfterReplaceError] = useState(false);
  const [showAreaCodeUnavailablePopup, setShowAreaCodeUnavailablePopup] = useState(false);
  const [showPhoneNumbersUpdatedPopup, setShowPhoneNumbersUpdatedPopup] = useState(false);

  const phoneNumbersMaxCount = 3;

  const aggregatedCallSettings = (() => {
    if (callSettings && callSettings.length > 0) {
      const aggregatedList = [];
      for (let i = 0; i < phoneNumbersMaxCount; i++) {
        if (callSettings.length > i * phoneNumbersMaxCount) {
          aggregatedList.push(
            callSettings.slice(i * phoneNumbersMaxCount, i * phoneNumbersMaxCount + phoneNumbersMaxCount),
          );
        }
      }
      return aggregatedList;
    }
    return callSettings;
  })();

  useEffect(() => {
    if (!selectedState && !selectedAreaCode) {
      const phoneNumber = callSettings && callSettingsId ? callSettings.find((item) => item.id == callSettingsId)?.outboundNumber : null;

      if (phoneNumber && stateAreaCodes && stateAreaCodes.length) {
        const areaCode = phoneNumber.slice(0, 3);

        let state = null;
        for (let stateAreaCode of stateAreaCodes) {
          if (stateAreaCode.areaCodes.find((item) => item == areaCode)) {
            state = stateAreaCode.state;
            break;
          }
        }

        if (state && areaCode) {
          setSelectedState({ id: state, name: state });
          setSelectedAreaCode({ id: areaCode, name: areaCode });

          window.isInitialStateAndAreaCode = true;
        }
      }
    }
  }, [callSettings, callSettingsId, stateAreaCodes]);

  useEffect(() => {
    if (selectedState != null && stateAreaCodes != null && stateAreaCodes.length > 0) {
      const stateWithAreaCodes = stateAreaCodes.find((item) => item.state == selectedState.id);

      if (stateWithAreaCodes) {
        setAreaCodesForSelectedState(stateWithAreaCodes.areaCodes.map((item) => ({ id: item, name: item })));
      } else {
        setAreaCodesForSelectedState([]);
      }
    } else {
      setAreaCodesForSelectedState([]);
    }

    if (!window.isInitialStateAndAreaCode) {
      setSelectedAreaCode(null);
    }
    setSelectedNumbers(null);
    setAreaCodeDoesntHaveEnoughNumbersError(false);
  }, [selectedState]);

  useEffect(() => {
    if (selectedState != null && selectedAreaCode != null) {
      dispatch(getCallSettingsAvailableOutboundNumbers(selectedState.id, selectedAreaCode.id));

      if (window.isInitialStateAndAreaCode) {
        window.isInitialStateAndAreaCode = null;
      }
    }
  }, [selectedAreaCode]);

  useEffect(() => {
    if (isAfterReplaceError && selectedState != null && selectedAreaCode != null) {
      dispatch(getCallSettingsAvailableOutboundNumbers(selectedState.id, selectedAreaCode.id));
    }
  }, [isAfterReplaceError]);

  useEffect(() => {
    if (availableOutboundNumbers && !isLoadingAvailableOutboundNumbers) {
      const numbersCount = availableOutboundNumbers.length;

      if (phoneNumbersMaxCount > 0) {
        if (numbersCount >= phoneNumbersMaxCount) {
          const randomlySelectedNumbers = getRandomItems(availableOutboundNumbers, phoneNumbersMaxCount);

          setSelectedNumbers(randomlySelectedNumbers);
          setAreaCodeDoesntHaveEnoughNumbersError(false);

          setShowPhoneNumbersUpdatedPopup(isAfterReplaceError);
        } else {
          if (isAfterReplaceError) {
            setShowAreaCodeUnavailablePopup(true);
            setSelectedState(null);
            setSelectedAreaCode(null);
            setSelectedNumbers(null);
            setIsAfterReplaceError(false);
          } else {
            setAreaCodeDoesntHaveEnoughNumbersError(true);
          }
        }
      }
    } else {
      setSelectedNumbers(null);
    }
  }, [availableOutboundNumbers, isLoadingAvailableOutboundNumbers]);

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) {
      return phoneNumber;
    }

    phoneNumber = phoneNumber.startsWith("+1") ? phoneNumber.substring(2, phoneNumber.length) : phoneNumber;

    if (phoneNumber.length < 10) {
      return phoneNumber;
    }

    return `(${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(3, 6)}-${phoneNumber.substring(6, phoneNumber.length)}`;
  };

  const removeUSCountryCode = (number) => {
    if (!number) return "";
    return number.startsWith("+1") ? number.slice(2) : number;
  };

  const handleInternalClose = () => {
    setSelectedState(null);
    setSelectedAreaCode(null);
    setSelectedNumbers(null);
    setAreaCodeDoesntHaveEnoughNumbersError(false);
    setIsAfterReplaceError(false);
    setShowAreaCodeUnavailablePopup(false);
    setShowPhoneNumbersUpdatedPopup(false);
    setResetKey((prev) => prev + 1);
    onClose();
  };

  const submitSelectedPhoneNumber = async () => {
    try {
      if (!selectedNumbers) {
        return;
      }

      setIsSubmitting(true);

      const result = await dispatch(
        replaceCallSettingsOutboundPhoneNumber(userId, {
          batchDialerNumbers: selectedNumbers.map((item) => ({
            trunkId: item.trunkid,
            number: removeUSCountryCode(item.number),
          })),
        }),
      );

      if (result && result.success) {
        setToastMessage("Your numbers have been successfully replaced.");
        setToastType("success");
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2000);

        handleInternalClose();
      } else {
        if (result.error && result.error.toLowerCase().includes("some numbers are not available anymore")) {
          setIsAfterReplaceError(true);
        } else {
          setToastMessage("Something went wrong. Please try again.");
          setToastType("error");
          setShowToast(true);
          setTimeout(() => setShowToast(false), 2000);

          handleInternalClose();
        }
      }
    } catch (err) {
      setToastMessage("Something went wrong. Please try again.");
      setToastType("error");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);

      handleInternalClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showAreaCodeUnavailablePopup) {
    return (
      <AreaCodeUnavailable
        isOpen={true}
        onSuccess={() => {
          setShowAreaCodeUnavailablePopup(false);
        }}
        onClose={() => {
          setShowAreaCodeUnavailablePopup(false);
        }}
      />
    );
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        isCloseButton={false}
        uniqId="ReplacePhoneNumberModal"
        borderRadius="12px"
        modalClassName={css.callsetupModal}
        className={classNames(css.clsAccPageModals, css.clsBillingModal)}
        onClose={handleInternalClose}
      >
        <div className={css.modalContainer}>
          <div className={css.headertxt}>
            <span>{!showPhoneNumbersUpdatedPopup ? <>Replace Phone Numbers</> : <>Phone Numbers Updated</>}</span>
          </div>
          <div className={css.instructionText}>
            {!showPhoneNumbersUpdatedPopup ? (
              <>
                <span>Your new numbers will use the same area code you selected last</span>
                <br />
                <span>time, unless you choose to change it.</span>
              </>
            ) : (
              <>
                <span>Some of your previously selected numbers are no longer available.</span>
                <br />
                <span>We’ve assigned new numbers for you below</span>
              </>
            )}
          </div>
          <div className={css.inputSection}>
            {!showPhoneNumbersUpdatedPopup && (
              <div className={css.dropdownRow}>
                <div>
                  <ResettableAutocompleteDropdown
                    placeholder="State"
                    key={`state-${resetKey}`}
                    items={stateAreaCodes.map((item) => ({ id: item.state, name: item.state }))}
                    onSelect={setSelectedState}
                    value={selectedState}
                  />
                </div>
                <div>
                  <ResettableAutocompleteDropdown
                    placeholder="Area Code"
                    key={`area-${resetKey}`}
                    items={areaCodesForSelectedState}
                    onSelect={setSelectedAreaCode}
                    disabled={!selectedState}
                    value={selectedAreaCode}
                  />
                </div>
              </div>
            )}
            {(() => {
              if (areaCodeDoesntHaveEnoughNumbersError) {
                return (
                  <div className={classNames(css.setupPhoneNumberNoStateAreaCodeSelectedContent, css.noteRow)}>
                    <div>
                      This area code is not currently available.
                      <br />
                      Please select a different one.
                    </div>
                  </div>
                );
              }
              if (!selectedState || !selectedAreaCode || !selectedNumbers) {
                return (
                  <div className={classNames(css.setupPhoneNumberNoStateAreaCodeSelectedContent, css.noteRow)}>
                    <div>
                      Select State and Area Code
                      <br />
                      to View Your Numbers
                    </div>
                  </div>
                );
              }
              return (
                <div className={css.inputWrapper}>
                  <div className={css.setupPhoneNumberContent}>
                    <div className={classNames(css.noteRow, css.setupPhoneNumberContentSection)}>
                      <div className={classNames(css.phonelabel, css.setupPhoneNumberPhoneLabel)}>
                        <span className={css.setupPhoneNumberPhoneLabelText}>Your Numbers</span>

                        <button className={css.replacePhoneNumberTag} disabled="true">
                          <span>Old</span>
                        </button>
                      </div>
                      <div className={css.setupPhoneNumberContentPhoneNumbers}>
                        {aggregatedCallSettings && callSettingsId
                          ? aggregatedCallSettings
                              .find((item) => item && item.length > 0 && item[0].id == callSettingsId)
                              ?.map((item) => formatPhoneNumber(item.outboundNumber))
                              .join("\n")
                          : null}
                      </div>
                    </div>

                    <div className={css.replacePhoneNumberContentArrowContainer}>
                      <img
                        className={css.replacePhoneNumberContentArrow}
                        src={RightArrow}
                        alt="RightArrow"
                        width={20}
                      />
                    </div>

                    <div className={classNames(css.noteRow, css.setupPhoneNumberContentSection)}>
                      <div className={classNames(css.phonelabel, css.setupPhoneNumberPhoneLabel)}>
                        <span className={css.setupPhoneNumberPhoneLabelText}>Your Numbers</span>

                        <button className={classNames(css.replacePhoneNumberTag, css.replacePhoneNumberTagOrange)} disabled="true">
                          <span>New</span>
                        </button>
                      </div>
                      <div className={css.setupPhoneNumberContentPhoneNumbers}>
                        {selectedNumbers
                          ? selectedNumbers.map((item) => formatPhoneNumber(item.number)).join("\n")
                          : null}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
          {!showPhoneNumbersUpdatedPopup && (
            <div className={css.noteRow}>
              <div>
                <img src={Notesimportanticon} alt="Notesimportanticon" />
              </div>
              <div>
                <span className={css.importText}>Numbers can be replaced every 7 days for no additional cost</span>
              </div>
            </div>
          )}
          <div className={css.buttonWrapper}>
            {!showPhoneNumbersUpdatedPopup && (
              <button className={css.cancelBtn} onClick={handleInternalClose}>
                <span className={css.cancelBtnLabel}>Cancel</span>
              </button>
            )}

            <LoadingButton
              loading={isSubmitting}
              disabled={!selectedNumbers || isSubmitting}
              onClick={submitSelectedPhoneNumber}
              variant={"teal"}
              isLarge={true}
            >
              <span className={css.connectYesBtnLabel}>Confirm</span>
            </LoadingButton>
          </div>
        </div>
      </Modal>
      <ToastNotification message={toastMessage} show={showToast} type={toastType} />
    </>
  );
};

export default ReplacePhoneNumber;
